// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Bundle } from '@/bundle/api/types';
import { LastUpdatedDate } from '@/components/last-updated-date';
import { Link } from '@/components/link';
import { OverflowTypography } from '@/components/overflow-typography';
import {
  Inventory2Outlined,
  SettingsOutlined,
  ShoppingCartOutlined,
} from '@/constants/hardfin-standard-icons';
import { SparseItem } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import {
  Chip,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

// Number of members shown before truncating to a "+N more" footer
const MEMBER_PREVIEW_LIMIT = 4;
const MEMBER_PREVIEW_TRUNCATED_COUNT = 3;

// Mirrors the items page's type filter icons
const MEMBER_ICONS_BY_TYPE: Record<
  SparseItem['type'],
  typeof Inventory2Outlined
> = {
  SERVICE: ShoppingCartOutlined,
  DEVICE: Inventory2Outlined,
  BULK: SettingsOutlined,
};

interface Props {
  bundle: Bundle;
}

/**
 * A single row in the bundles table.
 * Displays the bundle name, description, a member preview snapshot, and the
 * last updated timestamp. Archiving happens on the bundle detail page.
 */
export function BundleTableRow(props: Props) {
  const { bundle } = props;

  // Use disabled text color for archived bundles to visually indicate status
  const textColor = bundle.isArchived ? 'text.disabled' : 'text.secondary';

  const isTruncated = bundle.members.length > MEMBER_PREVIEW_LIMIT;
  const visibleMembers = bundle.members.slice(
    0,
    isTruncated ? MEMBER_PREVIEW_TRUNCATED_COUNT : MEMBER_PREVIEW_LIMIT
  );
  const hiddenMembers = bundle.members.slice(MEMBER_PREVIEW_TRUNCATED_COUNT);

  return (
    <TableRow hover>
      {/* Name - link to bundle detail page */}
      <TableCell>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Link
            to="/catalog/bundles/$bundleKey"
            params={{ bundleKey: bundle.key }}
            variant="subtitle2"
            color={bundle.isArchived ? 'text.disabled' : 'primary'}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {bundle.name}
          </Link>
          {bundle.isArchived && <Chip label="Archived" size="small" />}
        </Stack>
      </TableCell>
      {/* Description - full text available in a tooltip when clipped */}
      <TableCell>
        <OverflowTypography variant="body2" color={textColor} noWrap>
          {bundle.description ?? '\u2014'}
        </OverflowTypography>
      </TableCell>
      {/* Items - member preview snapshot */}
      <TableCell sx={{ verticalAlign: 'middle' }}>
        <Stack spacing={0.3}>
          {visibleMembers.map((member) => {
            const ItemIcon = MEMBER_ICONS_BY_TYPE[member.item.type];
            return (
              <Stack
                key={member.item.id}
                direction="row"
                alignItems="flex-start"
                flexWrap="wrap"
                gap={0.3}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={0.5}
                >
                  <ItemIcon sx={{ fontSize: '1rem' }} />
                  <>
                    <Typography variant="body2">{member.item.sku}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({member.item.name})
                    </Typography>
                  </>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    &times; {formatNumber(member.defaultQty)}
                  </Typography>
                </Stack>
              </Stack>
            );
          })}
          {isTruncated && (
            <Tooltip
              arrow
              title={
                <Stack spacing={0.5}>
                  {hiddenMembers.map((member) => {
                    const itemLabel = `${member.item.sku} (${member.item.name})`;
                    return (
                      <Typography key={member.item.id} variant="caption">
                        {itemLabel} &times; {formatNumber(member.defaultQty)}
                      </Typography>
                    );
                  })}
                </Stack>
              }
            >
              <Typography variant="body2" color="text.secondary">
                +{bundle.members.length - MEMBER_PREVIEW_TRUNCATED_COUNT} more
              </Typography>
            </Tooltip>
          )}
        </Stack>
      </TableCell>
      {/* Last updated - date with full timestamp and actor in the tooltip */}
      <TableCell>
        <LastUpdatedDate
          date={bundle.lastUpdatedAt}
          by={bundle.lastUpdatedBy}
          color={textColor}
        />
      </TableCell>
    </TableRow>
  );
}
